import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { BlockPatternId, LessonThemeId } from '@teach/ui'
import type { LessonUpdate } from '@/types/lesson'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { useUpdateBlocksLookMutation } from '@/features/editor/blocksApi'
import { lookUpdates, readLessonLook, type LessonLook } from '@/features/editor/lib/lessonLook'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { useIsDraftSourceSettling } from '@/features/moderation/lib/draftSourceSettling'
import { DRAFT_SETTLING_REFUSED } from '@/features/editor/lib/reviewLockText'
import { useHasUnsavedBlockDraft } from './aiSidebar/useUnsavedBlockGate'
import { isBlockLocked, useBlocksInRun, useIsBlockWritingRunActive } from './useBlocksInRun'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface LessonLookState {
  look: LessonLook
  /** Идёт запись — контролы держим погашенными ОБЕ фазы: и `PUT` урока, и
   *  рассылку по блокам. */
  isBusy: boolean
  /** Грязный черновик блока — контролы гасим. ЕДИНСТВЕННЫЙ сегодняшний
   *  потребитель хука (`LessonLookSection`) этого состояния получить не может:
   *  панель урока рендерится только при НЕвыбранном блоке (`BlockPanel`, ветка
   *  `draft === null`), а грязный черновик бывает лишь у выбранного — вдобавок
   *  `selectLesson` сбрасывает `selectedBlockId`. Поле оставлено, потому что
   *  честно у самого хука: он переиспользуемая единица, и следующая точка
   *  применения оформления (рядом с открытым блоком) упрётся в этот гейт
   *  по-настоящему. Секция его НЕ читает — вечно ложный запрет только врал бы. */
  isBlocked: boolean
  /** Блоки урока ПРИЕХАЛИ. Пока нет — контролы обязаны быть погашены: пустой
   *  `data` неотличим от «в уроке нет подходящих блоков», и клик по теме уходил
   *  бы одним лишь `PUT` урока. Тема читается из поля урока, поэтому такое
   *  расхождение переживает перезагрузку — оно не гонка, а порча данных. */
  isLoaded: boolean
  /** Студия переезжает на только что заведённый черновик: тела рассылки собраны из
   *  живой версии и затёрли бы черновик — контролы гасим, применение отказывает. */
  isSettling: boolean
  apply: (next: { theme: LessonThemeId; pattern: BlockPatternId | null }) => Promise<void>
}

/**
 * Оформление урока: один выбор → `PUT` урока + полный `PUT` каждого затронутого
 * блока. Гейтов прогона ДВА, ровно как у `handleReorder`: точечный (правка
 * блоков и перевод — блоки известны) и грубый `isBlockWritingRunActive`
 * (у картиночного прогона цели знает только сервер). Точечный сегодня отдельно
 * НЕДОСТИЖИМ — все три типа прогона уже в `BLOCK_WRITING_JOB_TYPES`, и грубый
 * гейт срабатывает раньше; оставлен парой к реордеру на случай, когда список
 * пишущих типов сузится. Частичное применение запрещено: урок из разнотемных
 * экранов хуже, чем неприменённый выбор.
 */
export const useLessonLook = (
  courseId: number,
  lessonId: number,
  lessonTheme: string | null | undefined,
  savePatch: (patch: LessonUpdate) => Promise<boolean>,
): LessonLookState => {
  const { data, isSuccess } = useGetLessonBlocksQuery(lessonId)
  const blocks = data ?? []
  const [updateBlocksLook, { isLoading }] = useUpdateBlocksLookMutation()
  const blocksInRun = useBlocksInRun(courseId)
  const isRunActive = useIsBlockWritingRunActive(courseId)
  const isFrozen = useIsCourseFrozen(courseId)
  const isSettling = useIsDraftSourceSettling(courseId)
  const hasUnsavedDraft = useHasUnsavedBlockDraft()
  // Отсекает второе применение РЕФОМ, а не флагом загрузки: флаг доезжает до
  // контролов только следующим рендером, а `isLoading` мутации блоков вообще
  // молчит всю первую фазу (`PUT` урока) — второй клик в это окно уходил бы
  // второй парой «урок + рассылка» по тем же блокам.
  const applyingRef = useRef(false)
  const [isApplying, setIsApplying] = useState(false)

  type Look = { theme: LessonThemeId; pattern: BlockPatternId | null }
  /** `true` — выбор записан (урок + рассылка либо рассылать нечего). */
  const write = async (next: Look): Promise<boolean> => {
    // Блоки не приехали (первый заход в урок, отказ `GET blocks`) — молчим
    // ЦЕЛИКОМ. Иначе `PUT` урока уходит один, экраны остаются старыми, а тема
    // читается из поля урока — расхождение переживает перезагрузку.
    if (!isSuccess) return false
    const updates = lookUpdates(blocks, next)
    // Грубый гейт истинен и на проверке, но прогона там нет — называем
    // настоящую причину, а не «прогон переписывает».
    if (isFrozen) {
      toast.error(messagesNow(messages).lookRefusedReview)
      return false
    }
    if (isSettling) {
      toast.error(DRAFT_SETTLING_REFUSED)
      return false
    }
    if (isRunActive || updates.some((u) => isBlockLocked(blocksInRun, u.id))) {
      toast.error(messagesNow(messages).lookRefusedAi)
      return false
    }
    // Урок первым: он и есть объявленный выбор автора. Не сохранился — рассылать
    // нечего (тост об отказе показал сам savePatch).
    if (!(await savePatch({ theme: next.theme }))) return false
    if (updates.length === 0) return true
    try {
      await updateBlocksLook({ lessonId, updates }).unwrap()
      return true
    } catch {
      // runSequentialPuts встаёт на первой ошибке: часть экранов отстала.
      // Числа не обещаем — после инвалидации автор видит их в ленте.
      toast.error(messagesNow(messages).lookPartial)
      return false
    }
  }

  // Выбор автора виден СРАЗУ (`shown`), а контролы не гаснут на время записи:
  // рассылка идёт по одному `PUT` на экран и на длинном уроке занимает секунды.
  // Клик во время записи не теряется и не уходит второй параллельной рассылкой —
  // он встаёт в очередь (`queuedRef`, только ПОСЛЕДНИЙ выбор), и после текущей
  // записи уходит он. Показ возвращается к серверу, когда тот догнал выбор или
  // запись отказала.
  const [shown, setShown] = useState<Look | null>(null)
  const queuedRef = useRef<Look | null>(null)
  const serverLook = readLessonLook(lessonTheme, blocks)

  useEffect(() => {
    if (
      shown &&
      !applyingRef.current &&
      serverLook.theme === shown.theme &&
      serverLook.pattern === shown.pattern
    ) {
      setShown(null)
    }
  }, [shown, serverLook.theme, serverLook.pattern])

  const apply = async (next: Look) => {
    setShown(next)
    if (applyingRef.current) {
      queuedRef.current = next
      return
    }
    applyingRef.current = true
    setIsApplying(true)
    try {
      let current: Look | null = next
      while (current) {
        const ok = await write(current)
        current = queuedRef.current
        queuedRef.current = null
        if (!ok && !current) setShown(null)
      }
    } finally {
      applyingRef.current = false
      setIsApplying(false)
    }
  }

  return {
    look: shown
      ? { ...serverLook, ...shown, isMixedPattern: false, isMixedTheme: false }
      : serverLook,
    isBusy: isLoading || isApplying,
    isBlocked: hasUnsavedDraft,
    isLoaded: isSuccess,
    isSettling,
    apply,
  }
}
